import { nanoid } from 'nanoid';
import * as React from 'react';
import type { FileRejection } from 'react-dropzone';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { getFormValues, reduxForm } from 'redux-form';
import { FieldArray } from 'redux-form';

import type { PendingAttachmentVM } from 'ab-viewModels/attachment/pendingAttachment.viewModel';

import { getId } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { BlobStorageContainer } from '@acceligentllc/shared/enums/blobStorageContainer';
import { FileType } from '@acceligentllc/shared/enums/fileType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import * as FORMS from 'af-constants/reduxForms';

import { Button, errorToast, loadingToast, successToast } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import FileUploadMessageWithTypes from 'af-components/Dropzone/FileUploadMessageWithTypes';
import DropzoneWithButton from 'af-components/DropzoneWithButton';

import * as AttachmentActions from 'af-actions/attachment';

import type { OwnProps as AttachmentArrayProps } from './AttachmentItems';
import AttachmentItems from './AttachmentItems';
import FileUploadFM from './formModel';

const TITLE = 'Upload New Document';

const ALLOWED_ATTACHMENT_FILE_TYPES_MIME = {
	'application/pdf': [
		FileType.PDF,
	],
};

interface OwnProps {
	closeModal: () => void;
	showModal: boolean;
	onSubmit: (form: FileUploadFM) => Promise<void>;
}

type FormOwnProps = ConnectedProps<typeof connector> & OwnProps;
type Props = FormOwnProps & InjectedFormProps<FileUploadFM>;

const FileUploadModal: React.FC<Props> = (props) => {
	const {
		closeModal,
		showModal,
		onSubmit,
		uploadPendingAttachments,
		deletePendingAttachments,
		change,
		currentFormValues,
		currentUserFullName,
		uploadProgress,
		initialize,
		handleSubmit,
	} = props;

	const [areAttachmentsInitialized, setAreAttachmentsInitialized] = React.useState(false);

	React.useEffect(() => {
		if (showModal) {
			initialize({
				attachments: [],
				uploadedAttachmentIds: [],
				uploadingAttachments: {},
			});
		}
	}, [initialize, showModal]);

	const submit = React.useCallback(async (form: FileUploadFM) => {
		await onSubmit(form);
		closeModal();
	}, [closeModal, onSubmit]);

	const onClose = React.useCallback(() => {
		// Don't close while uploading
		if (uploadProgress !== 100 && uploadProgress !== 0) {
			return;
		}
		deletePendingAttachments({ attachmentIds: currentFormValues.uploadedAttachmentIds });
		closeModal();
	}, [closeModal, currentFormValues?.uploadedAttachmentIds, deletePendingAttachments, uploadProgress]);

	const handleAttachmentsUpload = React.useCallback((attachmentIds: number[]) => {
		change('uploadedAttachmentIds', [...attachmentIds]);
	}, [change]);

	const handleAttachmentUploadStart = React.useCallback((correlationId: string) => {
		const nextUploadingAttachments = { ...currentFormValues.uploadingAttachments };
		nextUploadingAttachments[correlationId] = true;
		change('uploadingAttachments', nextUploadingAttachments);
	}, [change, currentFormValues]);

	const handleAttachmentUploadEnd = React.useCallback((correlationId: string) => {
		const nextUploadingAttachments = { ...currentFormValues.uploadingAttachments };
		delete nextUploadingAttachments[correlationId];
		change('uploadingAttachments', nextUploadingAttachments);
	}, [change, currentFormValues]);

	const handleAttachmentsChanged = React.useCallback((currentAttachments: FileUploadFM['attachments'], changedAttachments: FileUploadFM['attachments']) => {
		const addedAttachments: FileUploadFM['attachments'] = [...currentAttachments];
		// UGLY but needed. Simple table Field component cannot handle bulk change.
		// this was written as a workaround.
		changedAttachments.forEach((_att) => {
			addedAttachments.push(_att);
			change('attachments', addedAttachments);
		});
	}, [change]);

	const handleFileUpload = React.useCallback(async (acceptedFiles: File[], fileRejections: FileRejection[]) => {

		if (fileRejections.length > 0) {
			let message = `Uploading of ${fileRejections[0].file.name} failed.`;
			if (fileRejections[0].errors[0].code === 'file-invalid-type') {
				message = 'Uploading failed - File type not supported';
			}
			errorToast(fileRejections[0].file.name, { title: message, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });
			return;
		}

		const correlationId = nanoid();
		handleAttachmentUploadStart?.(correlationId);
		let uploadedAttachments: PendingAttachmentVM[] = [];

		const msgBase = acceptedFiles.length > 1
			? `${acceptedFiles[0].name} and ${acceptedFiles.length - 1} other file${acceptedFiles.length > 2 ? 's' : ''}`
			: acceptedFiles[0].name;

		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
		loadingToast(acceptedFiles[0].name, { title: msgBase, timestamp: startingTimestamp });

		try {
			uploadedAttachments = await uploadPendingAttachments({ requestFiles: acceptedFiles }, BlobStorageContainer.INVOICES_ATTACHMENTS);
			successToast(acceptedFiles[0].name, { title: `${msgBase} has successfully been uploaded.`, timestamp: startingTimestamp });
		} finally {
			handleAttachmentUploadEnd?.(correlationId);
		}

		const nextUploadedAttachmentIds = [...(currentFormValues.uploadedAttachmentIds ?? []), ...uploadedAttachments.map(getId)];
		const nextUploadedAttachments = uploadedAttachments.map((_att) => ({ ..._att, fileName: _att.name, uploadedBy: { fullName: currentUserFullName } }));
		handleAttachmentsChanged(currentFormValues.attachments ?? [], nextUploadedAttachments);
		handleAttachmentsUpload(nextUploadedAttachmentIds);
		setAreAttachmentsInitialized(true);
	}, [
		currentFormValues,
		handleAttachmentUploadEnd,
		handleAttachmentUploadStart,
		handleAttachmentsChanged,
		handleAttachmentsUpload,
		uploadPendingAttachments,
		currentUserFullName,
	]);

	const onDeleteAttachmentField = React.useCallback(async (index: number) => {
		const deletedAttachment = currentFormValues.attachments.splice(index, 1);
		currentFormValues.uploadedAttachmentIds.splice(index, 1);

		await deletePendingAttachments({ attachmentIds: [deletedAttachment[0].id] });

		change('attachments', [...currentFormValues.attachments]);
		change('uploadedAttachmentIds', [...currentFormValues.uploadedAttachmentIds]);
	}, [change, currentFormValues?.attachments, currentFormValues?.uploadedAttachmentIds, deletePendingAttachments]);

	const uploading = uploadProgress !== 100 && uploadProgress !== 0;
	const disabled = uploading || !currentFormValues?.uploadedAttachmentIds?.length;

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="info"
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header
				closeModal={onClose}
				title={TITLE}
			/>
			<CustomModal.Body>
				<FieldArray<AttachmentArrayProps>
					component={AttachmentItems}
					initialized={areAttachmentsInitialized}
					name="attachments"
					removeRow={onDeleteAttachmentField}
				/>
				<DropzoneWithButton
					allowedTypes={ALLOWED_ATTACHMENT_FILE_TYPES_MIME}
					onDrop={handleFileUpload}
				>
					<FileUploadMessageWithTypes
						allowedTypes={ALLOWED_ATTACHMENT_FILE_TYPES_MIME}
						uploading={uploading}
					/>
				</DropzoneWithButton>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<Button
					disabled={disabled}
					label="Upload"
					onClick={handleSubmit(submit)}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const currentFormValues = getFormValues(FORMS.FILE_UPLOAD_FORM)(state) as FileUploadFM;
	const currentUserFullName = `${userData.firstName} ${userData.lastName}`;

	return {
		currentFormValues: currentFormValues ?? null,
		currentUserFullName,
		uploadProgress: state.http.uploadProgress,
	};
}

function mapDispatchToProps() {
	return {
		uploadPendingAttachments: AttachmentActions.uploadPendingAttachments,
		deletePendingAttachments: AttachmentActions.deletePendingAttachments,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	reduxForm<FileUploadFM>({
		form: FORMS.FILE_UPLOAD_FORM,
		validate: FileUploadFM.validate,
	}),
	React.memo,
	connector
);

export default enhance(FileUploadModal);
